import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate, useParams, useOutletContext } from 'react-router-dom';
import { useWorkspace } from '@/contexts/WorkspaceContext';
import { onboardingApi } from '../services/onboardingApi';
import { supabase } from '@/integrations/supabase/client';
import { OnboardingProject } from '@/types/onboarding';
import { useToast } from '@/hooks/use-toast';
import { useTumUtilityVendors } from '@/hooks/useTumUtilityVendors';
import { useTumProperties } from '@/hooks/useTumProperties';
import { Loader2, Save, CheckCircle, RefreshCw, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import ExcelJS from 'exceljs';
// Import enhanced Stage 2 components
import {
  DataImportWorkspace,
  UploadSection,
  ImportStatusCard,
  RowActionsModal,
  ReuploadChangeDetection,
  StageNavigationBar,
  ContextualHelpPanel,
  EnhancedExcelTable,
  ColumnMappingModal,
} from '../components/stage2';
import type { EnhancedTableRow, TableColumn } from '../components/stage2';
import { generateEnhancedTemplate } from '../utils/generateEnhancedTemplate';
import ImportInsights from '../components/ImportInsights';
interface OutletContext {
  project: OnboardingProject | null;
  setProject: (project: OnboardingProject | null) => void;
  loadProject: () => Promise<void>;
}

interface ChecklistItem {
  id: string;
  label: string;
  status: 'complete' | 'in_progress' | 'pending';
  details?: string;
}

interface ImportBatchData {
  id: string;
  project_id: string;
  batch_type: string;
  filename: string;
  file_size_bytes?: number;
  created_at: string;
  status: string;
  total_rows?: number;
}

const Stage2DataImportEnhanced = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const { currentWorkspace } = useWorkspace();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { project } = useOutletContext<OutletContext>();

  // Core state
  const [importBatch, setImportBatch] = useState<ImportBatchData | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingError, setProcessingError] = useState<string | null>(null);
  const [accountRows, setAccountRows] = useState<EnhancedTableRow[]>([]);
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [showHelpPanel, setShowHelpPanel] = useState(false); // Collapsed by default for compact UI
  
  // Save state tracking
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [pendingSaves, setPendingSaves] = useState<Set<string>>(new Set());
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  // Modal states
  const [selectedRowForAction, setSelectedRowForAction] = useState<EnhancedTableRow | null>(null);
  const [showRowActionsModal, setShowRowActionsModal] = useState(false);
  const [showReuploadModal, setShowReuploadModal] = useState(false);
  const [reuploadData, setReuploadData] = useState<EnhancedTableRow[]>([]);
  const [reuploadFileName, setReuploadFileName] = useState('');
  
  // Column mapping modal state
  const [showColumnMappingModal, setShowColumnMappingModal] = useState(false);
  const [detectedColumns, setDetectedColumns] = useState<string[]>([]);
  const [detectedVendors, setDetectedVendors] = useState<string[]>([]);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  
  // Fetch vendors and properties
  const vendorsResult = useTumUtilityVendors();
  const vendors = vendorsResult.vendors?.map(v => ({ id: v.id, name: v.vendor_name })) || [];
  
  const propertiesResult = useTumProperties();
  const properties = propertiesResult.properties?.map(p => ({ 
    code: p.property_code || p.property_name, 
    name: p.property_name 
  })) || [];
  
  // Define table columns
  const tableColumns: TableColumn[] = useMemo(() => [
    { key: 'account_number', label: 'Account #', required: true, editable: true, tooltip: 'Unique utility account number' },
    { key: 'property_name', label: 'Property Name', required: true, editable: true, tooltip: 'Name of the property' },
    { key: 'property_code', label: 'Property Code', editable: true },
    { key: 'unit_number', label: 'Unit #', required: true, editable: true },
    { 
      key: 'commodity_type', 
      label: 'Commodity', 
      required: true, 
      editable: true, 
      type: 'select',
      options: ['Electricity', 'Water', 'Gas', 'Sewer', 'Trash', 'Storm Drain', 'Irrigation']
        .map(c => ({ value: c, label: c })),
      tooltip: 'Type of utility service'
    },
    { 
      key: 'vendor_name', 
      label: 'Vendor', 
      required: true, 
      editable: true, 
      type: 'select',
      options: vendors.map(v => ({ value: v.name, label: v.name })),
      tooltip: 'Utility provider name'
    },
    { key: 'meter_number', label: 'Meter #', editable: true },
    { key: 'gl_code', label: 'GL Code', editable: true },
    { key: 'service_address', label: 'Service Address', editable: true },
    { key: 'notes', label: 'Notes', editable: true },
  ], [vendors]);

  // Calculate import statistics
  const importStats = useMemo(() => {
    const total = accountRows.length;
    const success = accountRows.filter(r => r.status === 'success').length;
    const warning = accountRows.filter(r => r.status === 'warning').length;
    const failure = accountRows.filter(r => r.status === 'failure').length;
    const validated = accountRows.filter(r => r.canProceed).length;
    
    return {
      total,
      success,
      warning,
      failure,
      validated,
      pending: total - validated,
      successPercentage: total > 0 ? Math.round((success / total) * 100) : 0,
    };
  }, [accountRows]);

  // Calculate UNIQUE counts for Import Insights
  const uniqueInsights = useMemo(() => {
    const uniqueProperties = new Set<string>();
    const uniqueVendors = new Set<string>();
    const uniqueAccounts = new Set<string>(); // Account# + Commodity combo
    const uniqueUnits = new Set<string>();
    
    accountRows.forEach(row => {
      // Unique properties by name (case-insensitive)
      if (row.property_name?.trim()) {
        uniqueProperties.add(row.property_name.toLowerCase().trim());
      }
      
      // Unique vendors (exclude placeholders)
      if (row.vendor_name?.trim() && row.vendor_name !== '[Required]') {
        uniqueVendors.add(row.vendor_name.toLowerCase().trim());
      }
      
      // Unique accounts = Account# + Commodity Type combo
      if (row.account_number?.trim() && row.commodity_type?.trim()) {
        const accountKey = `${row.account_number.toLowerCase().trim()}|${row.commodity_type.toLowerCase().trim()}`;
        uniqueAccounts.add(accountKey);
      }
      
      // Unique units
      if (row.unit_number?.trim()) {
        uniqueUnits.add(row.unit_number.toLowerCase().trim());
      }
    });
    
    return {
      properties: uniqueProperties.size,
      vendors: uniqueVendors.size,
      accounts: uniqueAccounts.size,
      units: uniqueUnits.size,
    };
  }, [accountRows]);

  // Calculate stage progress
  const stageProgress = useMemo(() => {
    if (accountRows.length === 0) {
      return importBatch ? 50 : 0;
    }
    const fileWeight = importBatch ? 25 : 0;
    const importWeight = accountRows.length > 0 ? 25 : 0;
    const validationWeight = (importStats.validated / Math.max(importStats.total, 1)) * 50;
    return Math.round(fileWeight + importWeight + validationWeight);
  }, [importBatch, accountRows.length, importStats]);

  // Create checklist items
  const checklist: ChecklistItem[] = useMemo(() => {
    return [
      {
        id: 'file_uploaded',
        label: 'File uploaded',
        status: importBatch ? 'complete' : 'pending',
        details: importBatch ? `${importStats.total} accounts` : undefined,
      },
      {
        id: 'data_imported',
        label: 'Data imported successfully',
        status: accountRows.length > 0 ? 'complete' : (importBatch ? 'in_progress' : 'pending'),
      },
      {
        id: 'validation_progress',
        label: 'Validation in progress',
        status: importStats.validated === importStats.total && importStats.total > 0 
          ? 'complete' 
          : (importStats.validated > 0 ? 'in_progress' : 'pending'),
        details: importStats.total > 0 ? `${importStats.validated}/${importStats.total} accounts` : undefined,
      },
      {
        id: 'all_validated',
        label: 'All accounts validated',
        status: importStats.validated === importStats.total && importStats.total > 0 ? 'complete' : 'pending',
        details: importStats.pending > 0 ? `${importStats.pending} remaining` : undefined,
      },
    ];
  }, [importBatch, accountRows.length, importStats]);

  // Check if can proceed to next stage - simplified logic:
  // 1. Must have at least one account
  // 2. No critical failures (missing required fields)
  // Note: Warnings are OK to proceed
  const canProceed = useMemo(() => {
    console.log('🎯 CAN PROCEED CHECK:', {
      total: importStats.total,
      failure: importStats.failure,
      success: importStats.success,
      warning: importStats.warning,
      result: importStats.total > 0 && importStats.failure === 0
    });
    return importStats.total > 0 && importStats.failure === 0;
  }, [importStats]);

  // Load existing batch on mount
  useEffect(() => {
    if (project && projectId && projectId !== 'new') {
      loadExistingBatch();
    }
  }, [project, projectId]);

  const loadExistingBatch = async () => {
    if (!project || !currentWorkspace) return;
    
    try {
      const { data: batches } = await supabase
        .from('tum_onboarding_import_batches')
        .select('id, project_id, batch_type, filename, file_size_bytes, created_at, status, total_rows')
        .eq('project_id', project.id)
        .eq('batch_type', 'properties')
        .order('created_at', { ascending: false })
        .limit(1);
      
      if (batches && batches.length > 0) {
        setImportBatch(batches[0]);
        await loadAccountRowsFromBatch(batches[0].id);
      }
    } catch (error) {
      console.error('Error loading batch:', error);
    }
  };

  const loadAccountRowsFromBatch = async (batchId: string) => {
    try {
      // CRITICAL FIX: Only load rows that are STILL in Data Import stage
      // Exclude rows that have been advanced to Validation (ready_for_next_stage = true)
      // First get total count of rows still in data import stage
      const { count: totalCount, error: countError } = await supabase
        .from('tum_onboarding_staged_data')
        .select('id', { count: 'exact', head: true })
        .eq('batch_id', batchId)
        .or('ready_for_next_stage.is.null,ready_for_next_stage.eq.false'); // Only rows not yet advanced
      
      if (countError) {
        console.error('Error counting rows:', countError);
        throw countError;
      }
      
      console.log(`📊 Loading ${totalCount} rows still in Data Import stage (excluding advanced rows) for batch ${batchId}`);
      
      // Fetch all rows in chunks (Supabase PostgREST default max is 1000)
      const CHUNK_SIZE = 1000;
      const allStagedData: any[] = [];
      
      for (let offset = 0; offset < (totalCount || 0); offset += CHUNK_SIZE) {
        const { data: chunkData, error: chunkError } = await supabase
          .from('tum_onboarding_staged_data')
          .select('id, row_number, mapped_data, validation_status, validation_issues, ready_for_next_stage')
          .eq('batch_id', batchId)
          .or('ready_for_next_stage.is.null,ready_for_next_stage.eq.false') // Only rows not yet advanced
          .order('row_number', { ascending: true })
          .range(offset, offset + CHUNK_SIZE - 1);
        
        if (chunkError) {
          console.error(`Error loading chunk at offset ${offset}:`, chunkError);
          throw chunkError;
        }
        
        if (chunkData && chunkData.length > 0) {
          allStagedData.push(...chunkData);
          console.log(`📥 Loaded ${chunkData.length} rows (total: ${allStagedData.length}/${totalCount})`);
        }
      }
      
      const stagedData = allStagedData;
      console.log(`✅ Loaded ${stagedData.length} total rows from database`);
      
      if (stagedData && stagedData.length > 0) {
        const rows: EnhancedTableRow[] = stagedData.map((item: any) => {
          const mapped = item.mapped_data || {};
          const issues = item.validation_issues || [];
          
          // PHASE 4 FIX: Calculate status from ACTUAL data, not stale validation_issues
          const requiredFields = ['account_number', 'commodity_type', 'vendor_name'];
          const missingRequired = requiredFields.filter(f => {
            const val = mapped[f];
            return !val || val === '' || val === '[Required]';
          });
          
          // Determine status based on CURRENT mapped_data
          let status: 'success' | 'warning' | 'failure' = 'success';
          if (missingRequired.length > 0) {
            status = 'failure';
          } else {
            // Check for warnings from validation_issues (only if no missing required)
            const hasWarnings = issues.some((i: any) => i.severity === 'warning');
            if (hasWarnings) {
              status = 'warning';
            }
          }
          
          console.log(`📊 Row ${item.row_number} status:`, { 
            mapped_data: mapped, 
            missingRequired, 
            calculatedStatus: status,
            dbValidationStatus: item.validation_status 
          });
          
          return {
            id: item.id,
            row_number: item.row_number,
            account_number: mapped.account_number || '',
            property_name: mapped.property_name || '',
            property_code: mapped.property_code || '',
            unit_number: mapped.unit_number || '',
            commodity_type: mapped.commodity_type || mapped.utility_type || '',
            vendor_name: mapped.vendor_name || mapped.supplier_name || '',
            meter_number: mapped.meter_number || '',
            gl_code: mapped.gl_account_code || mapped.gl_code || '',
            service_address: mapped.service_address || '',
            billing_address: mapped.billing_address || '',
            notes: mapped.notes || '',
            status,
            canProceed: status !== 'failure',
            missingFields: missingRequired,
            validationErrors: issues.filter((i: any) => i.severity === 'error').map((i: any) => ({
              field: i.field || 'unknown',
              message: i.message || 'Validation error',
            })),
            warnings: issues.filter((i: any) => i.severity === 'warning').map((i: any) => ({
              field: i.field || 'unknown',
              message: i.message || 'Warning',
            })),
          };
        });
        
        setAccountRows(rows);
      }
    } catch (error) {
      console.error('Error loading account rows:', error);
    }
  };

  // Poll for batch completion
  const pollBatchStatus = async (batchId: string, maxAttempts = 10): Promise<boolean> => {
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      const { data: batch } = await supabase
        .from('tum_onboarding_import_batches')
        .select('status, rows_processed, total_rows')
        .eq('id', batchId)
        .single();
      
      if (batch?.rows_processed && batch.rows_processed > 0) {
        return true;
      }
      
      if (batch?.status === 'error' || batch?.status === 'failed') {
        throw new Error('File processing failed');
      }
      
      // Wait before next poll (increasing delay)
      await new Promise(resolve => setTimeout(resolve, 500 + (attempt * 300)));
    }
    return false;
  };

  // Parse file locally to detect columns and vendors
  const parseFileLocally = async (file: File): Promise<{ headers: string[]; vendors: string[] }> => {
    const fileExtension = file.name.split('.').pop()?.toLowerCase();
    
    if (fileExtension === 'xlsx' || fileExtension === 'xls') {
      // Parse Excel using ExcelJS
      const arrayBuffer = await file.arrayBuffer();
      const workbook = new ExcelJS.Workbook();
      await workbook.xlsx.load(arrayBuffer);
      
      // Find data sheet (skip Instructions)
      const worksheet = workbook.worksheets.find(ws => 
        !ws.name.toLowerCase().includes('instructions')
      ) || workbook.worksheets[0];
      
      if (!worksheet) {
        throw new Error('No worksheet found in file');
      }
      
      let headers: string[] = [];
      let vendors: string[] = [];
      
      // Get headers from first row
      const headerRow = worksheet.getRow(1);
      headerRow.eachCell((cell, colNumber) => {
        headers[colNumber - 1] = String(cell.value || '').trim();
      });
      headers = headers.filter(Boolean);
      
      // Find vendor column
      const vendorColIndex = headers.findIndex(h => 
        h.toLowerCase().includes('vendor') || h.toLowerCase().includes('supplier')
      );
      
      // Extract vendor values
      if (vendorColIndex >= 0) {
        worksheet.eachRow((row, rowNumber) => {
          if (rowNumber > 1) {
            const cellValue = String(row.getCell(vendorColIndex + 1).value || '').trim();
            if (cellValue && !cellValue.toLowerCase().includes('example')) {
              vendors.push(cellValue);
            }
          }
        });
      }
      
      return { headers, vendors };
    } else {
      // Parse CSV
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        
        reader.onload = (e) => {
          try {
            const text = e.target?.result as string;
            const lines = text.split('\n').filter(line => line.trim());
            
            if (lines.length === 0) {
              resolve({ headers: [], vendors: [] });
              return;
            }
            
            const headers = lines[0].split(',').map(h => h.trim().replace(/^"|"$/g, '')).filter(Boolean);
            
            // Find vendor column
            const vendorColIndex = headers.findIndex(h => 
              h.toLowerCase().includes('vendor') || h.toLowerCase().includes('supplier')
            );
            
            let vendors: string[] = [];
            if (vendorColIndex >= 0) {
              vendors = lines.slice(1)
                .map(line => {
                  const values = line.split(',');
                  return (values[vendorColIndex] || '').trim().replace(/^"|"$/g, '');
                })
                .filter(v => v && !v.toLowerCase().includes('example'));
            }
            
            resolve({ headers, vendors });
          } catch (error) {
            reject(error);
          }
        };
        
        reader.onerror = () => reject(new Error('Failed to read file'));
        reader.readAsText(file);
      });
    }
  };

  // Handle file upload - now shows column mapping modal first
  const handleFileUpload = async (file: File) => {
    if (!project || !currentWorkspace) {
      toast({
        title: 'Error',
        description: 'No project or workspace selected',
        variant: 'destructive',
      });
      return;
    }

    setIsProcessing(true);
    setProcessingError(null);
    
    try {
      // Parse file locally to get columns and vendors
      const { headers, vendors } = await parseFileLocally(file);
      
      if (headers.length === 0) {
        throw new Error('Could not detect columns in the file');
      }
      
      // Store file and detected data, show modal
      setPendingFile(file);
      setDetectedColumns(headers);
      setDetectedVendors(vendors);
      setShowColumnMappingModal(true);
      setIsProcessing(false);
      
    } catch (error: any) {
      const errorMsg = error.message || 'Failed to read file';
      setProcessingError(errorMsg);
      toast({
        title: 'File read failed',
        description: errorMsg,
        variant: 'destructive',
      });
      setIsProcessing(false);
    }
  };

  // Handle column mapping confirmation
  const handleColumnMappingProceed = async (
    mappings: Record<string, string>,
    vendorActions: Record<string, 'create' | 'ignore'>
  ) => {
    setShowColumnMappingModal(false);
    
    if (!pendingFile || !project || !currentWorkspace) {
      toast({
        title: 'Error',
        description: 'Missing file or project context',
        variant: 'destructive',
      });
      return;
    }

    // Validate mappings - filter out any invalid entries
    const validMappings: Record<string, string> = {};
    Object.entries(mappings).forEach(([sourceColumn, targetField]) => {
      if (targetField && 
          targetField !== 'unmapped' && 
          targetField.trim() !== '' &&
          targetField !== 'undefined' &&
          targetField !== 'null') {
        validMappings[sourceColumn] = targetField.trim();
      }
    });

    console.log('📋 Column Mapping - Valid mappings to send:', validMappings);
    console.log('📋 Column Mapping - Excluded mappings:', 
      Object.entries(mappings)
        .filter(([_, target]) => !target || target === 'unmapped' || target.trim() === '')
        .map(([source, _]) => source)
    );

    if (Object.keys(validMappings).length === 0) {
      toast({
        title: 'No valid mappings',
        description: 'Please map at least one column to a system field',
        variant: 'destructive',
      });
      return;
    }

    setIsProcessing(true);
    
    try {
      // Upload file and trigger processing with mappings
      // Note: vendor_actions is handled separately - auto_create_vendors flag is used
      const hasVendorsToCreate = Object.values(vendorActions).some(action => action === 'create');
      const batch = await onboardingApi.uploadImportFile(
        project.id, 
        'properties', 
        pendingFile,
        {
          field_mapping: validMappings, // Use validated mappings
          auto_create_vendors: hasVendorsToCreate,
          workspace_id: currentWorkspace.id,
        }
      );
      
      // Poll for completion
      const completed = await pollBatchStatus(batch.id);
      
      if (!completed) {
        throw new Error('Processing timed out. Please try again.');
      }
      
      // Validate
      let results;
      try {
        results = await onboardingApi.validateData(batch.id);
      } catch (validationError) {
        console.warn('Validation failed, loading data anyway:', validationError);
      }
      
      setImportBatch({
        id: batch.id,
        project_id: project.id,
        batch_type: 'properties',
        filename: pendingFile.name,
        file_size_bytes: pendingFile.size,
        created_at: new Date().toISOString(),
        status: 'validated',
        total_rows: results?.total_rows || 0,
      });
      
      // Load the imported data
      await loadAccountRowsFromBatch(batch.id);
      
      // Refresh vendor list if any vendors were created
      const hasCreatedVendors = Object.values(vendorActions).some(action => action === 'create');
      if (hasCreatedVendors) {
        vendorsResult.refetch?.();
      }
      
      toast({
        title: '✅ File imported successfully',
        description: results 
          ? `Imported ${results.total_rows} accounts. ${results.valid_rows} valid, ${results.invalid_rows} need attention.`
          : 'File processed. Review the imported data below.',
      });
      
      // Clear pending file
      setPendingFile(null);
      setDetectedColumns([]);
      setDetectedVendors([]);
      
    } catch (error: any) {
      const errorMsg = error.message || 'Failed to import file';
      setProcessingError(errorMsg);
      toast({
        title: 'Import failed',
        description: errorMsg,
        variant: 'destructive',
      });
    } finally {
      setIsProcessing(false);
    }
  };

  // Retry upload after error
  const handleRetryUpload = () => {
    setProcessingError(null);
  };

  // Unsaved changes warning - prompt before leaving
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges || pendingSaves.size > 0) {
        e.preventDefault();
        e.returnValue = 'You have unsaved changes. Are you sure you want to leave?';
        return e.returnValue;
      }
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges, pendingSaves]);

  // Keyboard shortcuts (Ctrl+S to force save)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        if (pendingSaves.size > 0) {
          toast({
            title: 'Saving...',
            description: `${pendingSaves.size} change(s) being saved`,
          });
        } else {
          toast({
            title: 'All changes saved',
            description: 'Your data is up to date',
          });
        }
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [pendingSaves, toast]);

  // Handle cell edit - Production-ready with immediate save, validation recalculation, and feedback
  const handleCellEdit = useCallback(async (rowId: string, field: string, value: any) => {
    const saveKey = `${rowId}-${field}`;
    const requiredFields = ['account_number', 'commodity_type', 'vendor_name'];
    
    // Track pending save
    setPendingSaves(prev => new Set(prev).add(saveKey));
    setSaveStatus('saving');
    setHasUnsavedChanges(true);
    
    // Update local state immediately (optimistic update)
    setAccountRows(prev => prev.map(row => {
      if (row.id === rowId) {
        const updated = { ...row, [field]: value };
        const missingRequired = requiredFields.filter(f => !updated[f] || updated[f] === '[Required]' || updated[f] === '');
        updated.status = missingRequired.length > 0 ? 'failure' : 'success';
        updated.canProceed = updated.status !== 'failure';
        updated.missingFields = missingRequired;
        return updated;
      }
      return row;
    }));
    
    // Retry logic for database save (max 3 attempts)
    const maxRetries = 3;
    let lastError: any = null;
    let success = false;
    
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        // Fetch current data
        const { data: existingData, error: fetchError } = await supabase
          .from('tum_onboarding_staged_data')
          .select('mapped_data, edit_count')
          .eq('id', rowId)
          .single();
        
        if (fetchError) {
          console.error(`Fetch error (attempt ${attempt}):`, fetchError);
          lastError = fetchError;
          if (attempt < maxRetries) {
            await new Promise(resolve => setTimeout(resolve, 300 * attempt));
            continue;
          }
          break;
        }
        
        if (existingData) {
          const currentEditCount = (existingData.edit_count as number) || 0;
          const updatedMappedData = {
            ...(existingData.mapped_data as Record<string, any> || {}),
            [field]: value,
          };
          
          // PHASE 2 FIX: Recalculate validation status and issues based on updated data
          const newValidationIssues: Array<{field: string; error_type: string; message: string; severity: 'error' | 'warning'}> = [];
          for (const reqField of requiredFields) {
            const fieldValue = updatedMappedData[reqField];
            if (!fieldValue || fieldValue === '' || fieldValue === '[Required]') {
              newValidationIssues.push({
                field: reqField,
                error_type: 'missing_required',
                message: `Required field ${reqField} is missing`,
                severity: 'error',
              });
            }
          }
          const newValidationStatus = newValidationIssues.length === 0 ? 'valid' : 'invalid';
          
          // Get current user
          const { data: { user } } = await supabase.auth.getUser();
          
          console.log('🔵 SAVE ATTEMPT:', { rowId, field, value, attempt, newValidationStatus, issueCount: newValidationIssues.length });
          
          // PHASE 2 FIX: Update BOTH mapped_data AND validation_status/validation_issues
          const { error: updateError } = await (supabase as any)
            .from('tum_onboarding_staged_data')
            .update({
              mapped_data: updatedMappedData,
              validation_status: newValidationStatus,
              validation_issues: newValidationIssues,
              last_edited_at: new Date().toISOString(),
              last_edited_by: user?.id,
              edit_count: currentEditCount + 1,
            })
            .eq('id', rowId);
          
          if (updateError) {
            console.error(`❌ Update error (attempt ${attempt}):`, updateError);
            lastError = updateError;
            if (attempt < maxRetries) {
              await new Promise(resolve => setTimeout(resolve, 300 * attempt));
              continue;
            }
            break;
          }
          
          // Verify the save was successful
          const { data: verifiedData, error: verifyError } = await (supabase as any)
            .from('tum_onboarding_staged_data')
            .select('mapped_data, validation_status')
            .eq('id', rowId)
            .single();
          
          if (verifyError || !verifiedData) {
            console.error(`❌ Verification error (attempt ${attempt}):`, verifyError);
            lastError = verifyError || new Error('Verification failed');
            if (attempt < maxRetries) {
              await new Promise(resolve => setTimeout(resolve, 300 * attempt));
              continue;
            }
            break;
          }
          
          const savedValue = verifiedData.mapped_data?.[field];
          if (savedValue !== value) {
            console.error(`❌ Verification mismatch: expected "${value}", got "${savedValue}"`);
            lastError = new Error('Data verification failed - value not persisted');
            if (attempt < maxRetries) {
              await new Promise(resolve => setTimeout(resolve, 300 * attempt));
              continue;
            }
            break;
          }
          
          // Success!
          console.log('✅ SAVE SUCCESS: Data AND validation_status verified in database', { 
            rowId, field, value, 
            savedValidationStatus: verifiedData.validation_status 
          });
          success = true;
          break;
        }
      } catch (error) {
        console.error(`Error saving cell edit (attempt ${attempt}):`, error);
        lastError = error;
        if (attempt < maxRetries) {
          await new Promise(resolve => setTimeout(resolve, 300 * attempt));
        }
      }
    }
    
    // Remove from pending saves
    setPendingSaves(prev => {
      const next = new Set(prev);
      next.delete(saveKey);
      return next;
    });
    
    if (success) {
      setSaveStatus('saved');
      setHasUnsavedChanges(false);
      
      // Clear saved status after 2 seconds
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
      saveTimeoutRef.current = setTimeout(() => {
        setSaveStatus('idle');
      }, 2000);
    } else {
      // All retries failed
      setSaveStatus('error');
      
      toast({
        title: 'Save failed',
        description: lastError?.message || 'Unable to save changes. Please try again.',
        variant: 'destructive',
      });
      
      // Reload data to revert optimistic update
      if (importBatch) {
        await loadAccountRowsFromBatch(importBatch.id);
      }
      
      // Reset error status after 3 seconds
      setTimeout(() => setSaveStatus('idle'), 3000);
    }
  }, [importBatch, toast]);
  
  // Handle vendor created - refresh vendor list
  const handleVendorCreated = useCallback((vendor: { id: string; name: string }) => {
    // Refresh vendor list
    vendorsResult.refetch?.();
    
    toast({
      title: 'Vendor created',
      description: `${vendor.name} has been added to your vendors`,
    });
  }, [vendorsResult, toast]);

  // Handle row actions
  const handleRowAction = (rowId: string) => {
    const row = accountRows.find(r => r.id === rowId);
    if (row) {
      setSelectedRowForAction(row);
      setShowRowActionsModal(true);
    }
  };

  const handleEditRow = async (rowId: string, updates: Partial<EnhancedTableRow>) => {
    for (const [field, value] of Object.entries(updates)) {
      await handleCellEdit(rowId, field, value);
    }
  };

  const handleDeleteRow = async () => {
    if (!selectedRowForAction) return;
    
    try {
      await supabase
        .from('tum_onboarding_staged_data')
        .delete()
        .eq('id', selectedRowForAction.id);
      
      setAccountRows(prev => prev.filter(r => r.id !== selectedRowForAction.id));
      setShowRowActionsModal(false);
      setSelectedRowForAction(null);
      
      toast({
        title: 'Row deleted',
        description: `Account ${selectedRowForAction.account_number} removed from import`,
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to delete row',
        variant: 'destructive',
      });
    }
  };

  // Handle bulk delete - batched to avoid URL length limits
  const handleBulkDelete = async (rowIds: string[]) => {
    if (rowIds.length === 0) return;
    
    const BATCH_SIZE = 50;
    let deletedCount = 0;
    const errors: string[] = [];
    
    try {
      for (let i = 0; i < rowIds.length; i += BATCH_SIZE) {
        const batch = rowIds.slice(i, i + BATCH_SIZE);
        
        const { error } = await supabase
          .from('tum_onboarding_staged_data')
          .delete()
          .in('id', batch);
        
        if (error) {
          errors.push(error.message);
        } else {
          deletedCount += batch.length;
        }
      }
      
      // Update local state with successfully deleted rows
      if (deletedCount > 0) {
        setAccountRows(prev => prev.filter(r => !rowIds.includes(r.id)));
        setSelectedRows([]);
      }
      
      if (errors.length > 0) {
        toast({
          title: 'Partial deletion',
          description: `${deletedCount} row(s) deleted. ${errors.length} batch(es) failed.`,
          variant: 'destructive',
        });
      } else {
        toast({
          title: 'Rows deleted',
          description: `${deletedCount} row(s) removed from import`,
        });
      }
    } catch (error: any) {
      console.error('Bulk delete error:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to delete rows',
        variant: 'destructive',
      });
    }
  };

  const handleValidateRow = async (): Promise<{
    isValid: boolean;
    errors: Array<{ field: string; message: string }>;
    warnings: Array<{ field: string; message: string }>;
  }> => {
    if (!selectedRowForAction) {
      return { isValid: false, errors: [{ field: 'row', message: 'Row not found' }], warnings: [] };
    }
    
    const row = selectedRowForAction;
    const errors: Array<{ field: string; message: string }> = [];
    const warnings: Array<{ field: string; message: string }> = [];
    
    if (!row.account_number) errors.push({ field: 'account_number', message: 'Account number is required' });
    if (!row.commodity_type) errors.push({ field: 'commodity_type', message: 'Commodity type is required' });
    if (!row.vendor_name) errors.push({ field: 'vendor_name', message: 'Vendor name is required' });
    if (!row.property_name && !row.property_code) {
      warnings.push({ field: 'property_name', message: 'Property name/code is recommended' });
    }
    
    return {
      isValid: errors.length === 0,
      errors,
      warnings,
    };
  };

  const handleSaveRow = async () => {
    setShowRowActionsModal(false);
    setSelectedRowForAction(null);
    toast({
      title: 'Changes saved',
      description: 'Row has been updated',
    });
  };

  // PHASE 3: Bulk recalculate validation for ALL rows
  const handleBulkRecalculateValidation = async () => {
    if (!importBatch) {
      toast({ title: 'No data to recalculate', variant: 'destructive' });
      return;
    }
    
    setIsProcessing(true);
    toast({ title: '🔄 Recalculating validation...', description: 'Please wait' });
    
    try {
      // Fetch all rows for this batch
      const { data: allRows, error: fetchError } = await (supabase as any)
        .from('tum_onboarding_staged_data')
        .select('id, mapped_data')
        .eq('batch_id', importBatch.id);
      
      if (fetchError) throw fetchError;
      
      const requiredFields = ['account_number', 'commodity_type', 'vendor_name'];
      let updatedCount = 0;
      let validCount = 0;
      let invalidCount = 0;
      
      // Process in batches of 50
      const BATCH_SIZE = 50;
      for (let i = 0; i < (allRows?.length || 0); i += BATCH_SIZE) {
        const batch = allRows.slice(i, i + BATCH_SIZE);
        
        for (const row of batch) {
          const data = row.mapped_data || {};
          const issues: Array<{field: string; error_type: string; message: string; severity: 'error' | 'warning'}> = [];
          
          for (const field of requiredFields) {
            const val = data[field];
            if (!val || val === '' || val === '[Required]') {
              issues.push({
                field,
                error_type: 'missing_required',
                message: `Required field ${field} is missing`,
                severity: 'error',
              });
            }
          }
          
          const newStatus = issues.length === 0 ? 'valid' : 'invalid';
          if (newStatus === 'valid') validCount++;
          else invalidCount++;
          
          await (supabase as any)
            .from('tum_onboarding_staged_data')
            .update({
              validation_status: newStatus,
              validation_issues: issues,
            })
            .eq('id', row.id);
          
          updatedCount++;
        }
      }
      
      // Update batch summary
      await (supabase as any)
        .from('tum_onboarding_import_batches')
        .update({
          rows_success: validCount,
          rows_failed: invalidCount,
          rows_warning: 0,
          status: 'validated',
        })
        .eq('id', importBatch.id);
      
      // Reload data to reflect changes
      await loadAccountRowsFromBatch(importBatch.id);
      
      toast({ 
        title: '✅ Validation recalculated', 
        description: `${updatedCount} rows updated: ${validCount} valid, ${invalidCount} need attention` 
      });
    } catch (error: any) {
      console.error('Bulk recalculate error:', error);
      toast({ 
        title: 'Recalculation failed', 
        description: error.message || 'Unknown error',
        variant: 'destructive' 
      });
    } finally {
      setIsProcessing(false);
    }
  };

  // PHASE 5: Bulk advance valid rows to next stage (with comprehensive logging & error handling)
  const handleBulkAdvanceToValidation = async () => {
    console.log('🚀 BULK ADVANCE: Starting advance to validation...');
    console.log('📊 Total rows loaded in UI:', accountRows.length);
    
    if (!importBatch) {
      toast({ 
        title: 'Error', 
        description: 'No import batch found',
        variant: 'destructive' 
      });
      return;
    }
    
    setIsProcessing(true);
    
    try {
      // CRITICAL FIX: Query ALL rows from database directly, not just what's in UI state
      // This ensures we process all 5,622 rows, not just the 1,000 loaded in UI
      console.log('🔍 Querying ALL rows from database for batch:', importBatch.id);
      
      // First, get total count of success/warning rows (non-failure rows)
      // These are rows with validation_status = 'valid' or 'warning' (not 'invalid' or 'failure')
      const { count: totalCount, error: countError } = await (supabase as any)
        .from('tum_onboarding_staged_data')
        .select('id', { count: 'exact', head: true })
        .eq('batch_id', importBatch.id)
        .in('validation_status', ['valid', 'warning']); // Only valid/warning rows can advance
      
      if (countError) {
        console.error('❌ Count query failed:', countError);
        throw new Error(`Failed to count rows: ${countError.message}`);
      }
      
      console.log(`📊 Total rows in database with valid/warning status: ${totalCount}`);
      
      if (!totalCount || totalCount === 0) {
        toast({ 
          title: 'No rows to advance', 
          description: 'No rows with valid or warning status found in database',
          variant: 'destructive' 
        });
        setIsProcessing(false);
        return;
      }
      
      // Fetch ALL row IDs in chunks (Supabase PostgREST has a default limit, so we paginate)
      const FETCH_CHUNK_SIZE = 1000; // Supabase default max
      const UPDATE_BATCH_SIZE = 50; // Smaller batches for updates
      const allRowIds: string[] = [];
      
      // Fetch all row IDs in chunks
      for (let offset = 0; offset < totalCount; offset += FETCH_CHUNK_SIZE) {
        const { data: chunkData, error: fetchError } = await (supabase as any)
          .from('tum_onboarding_staged_data')
          .select('id')
          .eq('batch_id', importBatch.id)
          .in('validation_status', ['valid', 'warning'])
          .order('row_number', { ascending: true })
          .range(offset, offset + FETCH_CHUNK_SIZE - 1);
        
        if (fetchError) {
          console.error(`❌ Failed to fetch chunk at offset ${offset}:`, fetchError);
          throw new Error(`Failed to fetch rows: ${fetchError.message}`);
        }
        
        if (chunkData && chunkData.length > 0) {
          allRowIds.push(...chunkData.map((r: any) => r.id));
          console.log(`📥 Fetched ${chunkData.length} row IDs (total so far: ${allRowIds.length}/${totalCount})`);
        }
      }
      
      console.log(`✅ Fetched ${allRowIds.length} total row IDs to advance`);
      
      if (allRowIds.length === 0) {
        toast({ 
          title: 'No rows to advance', 
          description: 'No valid rows found to advance',
          variant: 'destructive' 
        });
        setIsProcessing(false);
        return;
      }
      
      // Now update all rows in batches
      const totalBatches = Math.ceil(allRowIds.length / UPDATE_BATCH_SIZE);
      let successCount = 0;
      let errorCount = 0;
      const failedBatches: number[] = [];
      const failedRowIds: string[] = [];
      
      console.log(`📦 Processing ${allRowIds.length} rows in ${totalBatches} update batches (batch size: ${UPDATE_BATCH_SIZE})`);
      
      // Process in batches with per-batch error handling
      for (let i = 0; i < allRowIds.length; i += UPDATE_BATCH_SIZE) {
        const batchNumber = Math.floor(i / UPDATE_BATCH_SIZE) + 1;
        const batch = allRowIds.slice(i, i + UPDATE_BATCH_SIZE);
        
        if (batchNumber % 10 === 0 || batchNumber === 1 || batchNumber === totalBatches) {
          console.log(`📦 Processing update batch ${batchNumber}/${totalBatches}: ${batch.length} rows`);
        }
        
        // CRITICAL FIX: Use ready_for_next_stage flag instead of import_status
        // import_status CHECK constraint only allows: 'pending', 'imported', 'skipped', 'failed'
        // The ready_for_next_stage column was specifically added for this purpose
        const updateData: any = { 
          ready_for_next_stage: true
        };
        
        const { data, error } = await (supabase as any)
          .from('tum_onboarding_staged_data')
          .update(updateData)
          .in('id', batch)
          .select('id');
        
        if (error) {
          console.error(`❌ Batch ${batchNumber} failed:`, error);
          console.error(`❌ Error details:`, {
            message: error.message,
            code: error.code,
            details: error.details,
            hint: error.hint,
            batchSize: batch.length,
            firstRowId: batch[0]
          });
          errorCount += batch.length;
          failedBatches.push(batchNumber);
          failedRowIds.push(...batch);
          // Continue processing remaining batches - don't abort
        } else {
          const updatedCount = data?.length || 0;
          successCount += updatedCount;
          if (updatedCount !== batch.length) {
            console.warn(`⚠️ Batch ${batchNumber}: Expected ${batch.length} updates, got ${updatedCount}`);
          }
          if (batchNumber % 10 === 0 || batchNumber === 1 || batchNumber === totalBatches) {
            console.log(`✅ Batch ${batchNumber} succeeded: ${updatedCount} rows updated`);
          }
        }
      }
      
      console.log(`📊 BULK ADVANCE SUMMARY: Success=${successCount}, Errors=${errorCount}, Failed batches=${failedBatches.length}`);
      
      // Verify final count from database (server-side truth)
      const { count: verifiedCount, error: verifyError } = await (supabase as any)
        .from('tum_onboarding_staged_data')
        .select('id', { count: 'exact', head: true })
        .eq('batch_id', importBatch.id)
        .eq('ready_for_next_stage', true);
      
      if (verifyError) {
        console.error('❌ Verification query failed:', verifyError);
      } else {
        console.log(`🔍 VERIFICATION: Database confirms ${verifiedCount} rows with import_status='ready_for_validation'`);
      }
      
      // Show appropriate toast based on results
      // CRITICAL FIX: Use verifiedCount from database as the source of truth, not successCount
      // This ensures we show the ACTUAL number of rows that were successfully advanced
      const actualAdvancedCount = verifiedCount || successCount;
      
      if (failedBatches.length > 0) {
        toast({ 
          title: '⚠️ Partial advance completed', 
          description: `${actualAdvancedCount} of ${allRowIds.length} rows successfully advanced to validation stage. ${errorCount} rows failed.`,
          variant: 'destructive',
          duration: 10000
        });
        console.error('❌ Failed row IDs:', failedRowIds.slice(0, 20)); // Log first 20 failed IDs
      } else {
        toast({ 
          title: '✅ Successfully advanced to validation', 
          description: `${actualAdvancedCount} row${actualAdvancedCount !== 1 ? 's' : ''} successfully advanced to validation stage`,
          duration: 5000
        });
      }
      
      // Refresh the UI data to reflect changes
      if (importBatch) {
        await loadAccountRowsFromBatch(importBatch.id);
      }
      
      // Navigate to next stage
      handleNext();
    } catch (error: any) {
      console.error('❌ BULK ADVANCE CRITICAL ERROR:', error);
      toast({ 
        title: 'Advance failed', 
        description: error.message || 'Unknown error. Check console for details.',
        variant: 'destructive',
        duration: 10000
      });
    } finally {
      setIsProcessing(false);
    }
  };

  // Handle re-upload
  const handleReupload = () => {
    // Open file picker for re-upload
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.csv,.xlsx,.xls';
    input.onchange = async (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (file) {
        // For now, just re-import the file
        await handleFileUpload(file);
      }
    };
    input.click();
  };

  // Handle reupload confirmation
  const handleConfirmReupload = async (changes: any) => {
    setShowReuploadModal(false);
    toast({
      title: 'Changes applied',
      description: `Updated ${changes?.updated?.length || 0} rows, added ${changes?.added?.length || 0}, deleted ${changes?.deleted?.length || 0}`,
    });
  };

  // Navigation handlers
  const handleBack = () => {
    navigate(`/w/${currentWorkspace?.id}/dashboard/tum/onboarding/project/${projectId}/stage-1`);
  };

  const handleSaveProgress = async () => {
    toast({
      title: 'Progress saved',
      description: 'Your changes have been saved automatically',
    });
  };

  const handleNext = async () => {
    if (!canProceed) {
      toast({
        title: 'Validation required',
        description: 'Please fix all errors before proceeding',
        variant: 'destructive',
      });
      return;
    }
    
    if (project) {
      await onboardingApi.updateProject(project.id, {
        current_stage: 'validation',
      });
      navigate(`/w/${currentWorkspace?.id}/dashboard/tum/onboarding/project/${projectId}/stage-3`);
    }
  };

  // Export data to CSV for download
  const handleExportData = async () => {
    if (!importBatch || accountRows.length === 0) {
      toast({
        title: 'No data to export',
        description: 'Please import data first before exporting.',
        variant: 'destructive',
      });
      return;
    }

    try {
      // Build CSV content
      const headers = [
        'onboarding_task_id',
        'row_number',
        'account_number',
        'property_name',
        'property_code',
        'unit_number',
        'commodity_type',
        'vendor_name',
        'meter_number',
        'gl_code',
        'service_address',
        'billing_address',
        'notes',
        'status',
        'action',
      ];
      
      const csvRows = [
        headers.join(','),
        ...accountRows.map((row, index) => {
          const values = [
            row.id,
            index + 1,
            row.account_number || '',
            row.property_name || '',
            row.property_code || '',
            row.unit_number || '',
            row.commodity_type || '',
            row.vendor_name || '',
            row.meter_number || '',
            row.gl_code || '',
            row.service_address || '',
            row.billing_address || '',
            row.notes || '',
            row.status || '',
            '', // action column - user will fill
          ].map(val => {
            const strVal = String(val);
            if (strVal.includes(',') || strVal.includes('"') || strVal.includes('\n')) {
              return `"${strVal.replace(/"/g, '""')}"`;
            }
            return strVal;
          });
          return values.join(',');
        }),
      ];
      
      const csvContent = csvRows.join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `onboarding-data-${project?.customer_name || 'export'}-${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast({
        title: 'Export successful',
        description: `Exported ${accountRows.length} accounts to CSV. Use the "action" column to mark rows as EDIT, DELETE, or leave blank.`,
      });
    } catch (error: any) {
      toast({
        title: 'Export failed',
        description: error.message || 'Failed to export data',
        variant: 'destructive',
      });
    }
  };

  // Re-import data from CSV - auto-detects fresh files vs exported files
  const handleReimportData = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.csv,.xlsx,.xls';
    input.onchange = async (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (!file) return;
      
      try {
        console.log('📥 UPLOAD/REPLACE: Processing file:', file.name);
        
        const text = await file.text();
        const lines = text.split('\n').filter(l => l.trim());
        if (lines.length < 2) {
          toast({
            title: 'Invalid file',
            description: 'File must have at least a header and one data row.',
            variant: 'destructive',
          });
          return;
        }
        
        const headers = lines[0].split(',').map(h => h.trim().replace(/^"|"$/g, ''));
        const hasTaskIdColumn = headers.includes('onboarding_task_id');
        
        console.log('📥 UPLOAD/REPLACE: Detected headers:', headers);
        console.log('📥 UPLOAD/REPLACE: Has onboarding_task_id column:', hasTaskIdColumn);
        
        // Auto-detect: If file does NOT have onboarding_task_id, treat as fresh upload
        if (!hasTaskIdColumn) {
          console.log('📥 UPLOAD/REPLACE: No task ID column - processing as FRESH UPLOAD');
          toast({
            title: 'Processing as new file',
            description: 'Detected fresh CSV without task IDs. Processing as new import...',
          });
          await handleFileUpload(file);
          return;
        }
        
        // Has onboarding_task_id - process as re-import (update existing rows)
        console.log('📥 UPLOAD/REPLACE: Has task ID column - processing as RE-IMPORT (update existing)');
        
        const taskIdIndex = headers.indexOf('onboarding_task_id');
        const actionIndex = headers.indexOf('action');
        
        let updated = 0;
        let deleted = 0;
        const errors: string[] = [];
        
        for (let i = 1; i < lines.length; i++) {
          const values = lines[i].split(',').map(v => v.trim().replace(/^"|"$/g, ''));
          const taskId = values[taskIdIndex];
          const action = actionIndex !== -1 ? values[actionIndex]?.toUpperCase() : '';
          
          if (!taskId) continue;
          
          if (action === 'DELETE') {
            // Delete the row
            const { error } = await supabase
              .from('tum_onboarding_staged_data')
              .update({ import_status: 'skipped' })
              .eq('id', taskId);
            
            if (error) {
              errors.push(`Row ${i}: ${error.message}`);
            } else {
              deleted++;
            }
          } else {
            // Update the row with new values
            const mappedData: Record<string, any> = {};
            headers.forEach((header, idx) => {
              if (!['onboarding_task_id', 'row_number', 'action', 'status'].includes(header) && values[idx]) {
                mappedData[header] = values[idx];
              }
            });
            
            if (Object.keys(mappedData).length > 0) {
              const { error } = await (supabase as any)
                .from('tum_onboarding_staged_data')
                .update({ mapped_data: mappedData })
                .eq('id', taskId);
              
              if (error) {
                errors.push(`Row ${i}: ${error.message}`);
              } else {
                updated++;
              }
            }
          }
        }
        
        console.log('📥 RE-IMPORT COMPLETE: Updated:', updated, 'Deleted:', deleted, 'Errors:', errors.length);
        
        // Reload data
        if (importBatch) {
          await loadAccountRowsFromBatch(importBatch.id);
        }
        
        toast({
          title: 'Re-import complete',
          description: `Updated ${updated} rows, deleted ${deleted} rows.${errors.length > 0 ? ` ${errors.length} error(s).` : ''}`,
          variant: errors.length > 0 ? 'destructive' : 'default',
        });
      } catch (error: any) {
        console.error('📥 UPLOAD/REPLACE ERROR:', error);
        toast({
          title: 'Upload failed',
          description: error.message || 'Failed to process file',
          variant: 'destructive',
        });
      }
    };
    input.click();
  };

  // Bulk action handler
  const handleBulkAction = async (action: string, selectedIds: string[]) => {
    toast({
      title: `Bulk action: ${action}`,
      description: `Applied to ${selectedIds.length} rows`,
    });
  };

  return (
    <DataImportWorkspace 
      projectId={projectId || ''} 
      projectName={project?.customer_name || 'New Project'}
    >
      {/* Contextual Help Panel - collapsed by default, serves as instructions */}
      <ContextualHelpPanel 
        isExpanded={showHelpPanel} 
        onToggle={setShowHelpPanel}
        onDownloadTemplate={generateEnhancedTemplate}
      />
      
      {/* Upload Section with processing state and retry */}
      <UploadSection
        onFileSelect={handleFileUpload}
        onDownloadTemplate={generateEnhancedTemplate}
        onRetry={handleRetryUpload}
        acceptedTypes={['.csv', '.xlsx', '.xls']}
        maxSizeMB={100}
        disabled={isProcessing}
        isProcessing={isProcessing}
        processingError={processingError}
      />
      
      {/* Import Insights - Show unique counts after data loaded */}
      {accountRows.length > 0 && (
        <ImportInsights
          accounts={uniqueInsights.accounts}
          properties={uniqueInsights.properties}
          vendors={uniqueInsights.vendors}
          units={uniqueInsights.units}
          success={importStats.success}
          warning={importStats.warning}
          failure={importStats.failure}
          total={importStats.total}
        />
      )}
      
      {/* Import Status Card - Show after upload */}
      {importBatch && (
        <ImportStatusCard
          fileName={importBatch.filename}
          fileSize={importBatch.file_size_bytes}
          uploadedAt={importBatch.created_at}
          totalRows={importStats.total}
          importedRows={importStats.success + importStats.warning}
          skippedRows={importStats.failure}
          warningRows={importStats.warning}
          errorRows={importStats.failure}
          onViewErrors={() => {
            toast({
              title: 'Filtering errors',
              description: 'Showing accounts that need attention',
            });
          }}
          onReupload={handleReupload}
        />
      )}
      
      {/* Bulk Actions - Show after data is loaded */}
      {accountRows.length > 0 && (
        <div className="flex flex-wrap gap-2 p-4 bg-muted/50 rounded-lg border">
          <Button
            variant="outline"
            size="sm"
            onClick={handleBulkRecalculateValidation}
            disabled={isProcessing}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${isProcessing ? 'animate-spin' : ''}`} />
            Recalculate Validation
          </Button>
          
          <Button
            variant="default"
            size="sm"
            onClick={handleBulkAdvanceToValidation}
            disabled={isProcessing || importStats.failure > 0}
            className="bg-primary"
          >
            <ArrowRight className="h-4 w-4 mr-2" />
            Advance {importStats.success + importStats.warning} Rows to Validation
          </Button>
          
          {importStats.failure > 0 && (
            <span className="text-xs text-muted-foreground self-center ml-2">
              ⚠️ {importStats.failure} row(s) have errors that must be fixed first
            </span>
          )}
        </div>
      )}
      
      {/* Enhanced Excel Table - Show after data is loaded */}
      {accountRows.length > 0 && (
        <EnhancedExcelTable
          rows={accountRows}
          columns={tableColumns}
          selectedRows={selectedRows}
          onSelectionChange={setSelectedRows}
          onCellEdit={handleCellEdit}
          onRowAction={handleRowAction}
          vendors={vendors}
          properties={properties}
          onVendorCreated={handleVendorCreated}
          onBulkDelete={handleBulkDelete}
        />
      )}
      
      {/* Stage Navigation Bar */}
      <StageNavigationBar
        status={
          accountRows.length === 0 
            ? (importBatch ? 'in_progress' : 'not_started')
            : (canProceed ? 'completed' : 'in_progress')
        }
        progress={stageProgress}
        checklist={checklist}
        canProceed={canProceed}
        onBack={handleBack}
        onSave={handleSaveProgress}
        onNext={handleNext}
        onExport={handleExportData}
        onReimport={handleReimportData}
        hasData={accountRows.length > 0}
      />
      
      {/* Row Actions Modal */}
      {selectedRowForAction && (
        <RowActionsModal
          isOpen={showRowActionsModal}
          onClose={() => {
            setShowRowActionsModal(false);
            setSelectedRowForAction(null);
          }}
          row={{
            id: selectedRowForAction.id,
            account_number: selectedRowForAction.account_number,
            property_name: selectedRowForAction.property_name,
            unit_number: selectedRowForAction.unit_number,
            commodity_type: selectedRowForAction.commodity_type,
            vendor_name: selectedRowForAction.vendor_name,
          }}
          onEdit={handleEditRow}
          onValidate={handleValidateRow}
          onDelete={handleDeleteRow}
          onSave={handleSaveRow}
          commodities={['Electricity', 'Water', 'Gas', 'Sewer', 'Trash', 'Storm Drain', 'Irrigation']}
          vendors={vendors}
        />
      )}
      
      {/* Re-upload Change Detection Modal */}
      <ReuploadChangeDetection
        isOpen={showReuploadModal}
        onClose={() => {
          setShowReuploadModal(false);
          setReuploadData([]);
        }}
        currentData={accountRows.map(r => ({
          id: r.id,
          account_number: String(r.account_number || ''),
          property_name: r.property_name,
          unit_number: r.unit_number,
          commodity_type: r.commodity_type,
          vendor_name: r.vendor_name,
        }))}
        newFileName={reuploadFileName}
        newData={reuploadData.map(r => ({
          id: r.id,
          account_number: String(r.account_number || ''),
          property_name: r.property_name,
          unit_number: r.unit_number,
          commodity_type: r.commodity_type,
          vendor_name: r.vendor_name,
        }))}
        onConfirm={handleConfirmReupload}
      />
      
      {/* Column Mapping Modal */}
      <ColumnMappingModal
        open={showColumnMappingModal}
        onClose={() => {
          setShowColumnMappingModal(false);
          setPendingFile(null);
          setDetectedColumns([]);
          setDetectedVendors([]);
        }}
        onProceed={handleColumnMappingProceed}
        sourceColumns={detectedColumns}
        existingVendors={vendors}
        detectedVendors={detectedVendors}
        isLoading={isProcessing}
      />
    </DataImportWorkspace>
  );
};

export default Stage2DataImportEnhanced;
